import axios from './axios';
import { templateAPI } from './templateAPI';

// Auth endpoints
export const authAPI = {
  login: (credentials) => axios.post('/auth/login', credentials),
  verifyOtp: (data) => axios.post('/auth/verify-otp', data),
  logout: () => axios.post('/auth/logout'),
  getMe: () => axios.get('/auth/me'),
};

// User management endpoints
export const userAPI = {
  getAll: (params?: any) => axios.get('/users', { params }),
  getById: (id) => axios.get(`/users/${id}`),
  create: (data) => axios.post('/users', data),
  update: (id, data) => axios.put(`/users/${id}`, data),
  delete: (id) => axios.delete(`/users/${id}`),
  toggleStatus: (id) => axios.post(`/users/${id}/toggle-status`),
  promote: (id, role) => axios.post(`/users/${id}/promote`, { role }),
  resetPassword: (id, data) => axios.post(`/users/${id}/reset-password`, data),
  promoteUser: (id, data) => axios.post(`/users/${id}/promote`, data),
  getStats: () => axios.get('/users/stats'),
  getOrganizations: () => axios.get('/users/organizations'),
  getOrganizationStats: (id) => axios.get(`/organizations/${id}/stats`),
};

export const organizationRolesAPI = {
  getAll: () => axios.get('/organizations/roles'),
  create: (data) => axios.post('/organizations/roles', data),
  update: (id, data) => axios.put(`/organizations/roles/${id}`, data),
  delete: (id) => axios.delete(`/organizations/roles/${id}`),
};

// Event endpoints
export const eventAPI = {
  getAll: (params?: any) => axios.get('/events', { params }),
  getById: (id) => axios.get(`/events/${id}`),
  getDefault: () => axios.get('/events/default/current'),
  create: (data) => axios.post('/events', data),
  update: (id, data) => axios.put(`/events/${id}`, data),
  delete: (id) => axios.delete(`/events/${id}`),
  duplicate: (id) => axios.post(`/events/${id}/duplicate`),
  setDefault: (id) => axios.post(`/events/${id}/set-default`),
  unsetDefault: () => axios.delete('/events/default/unset'),

  // Public registration configuration (no auth required)
  getRegistrationConfig: (eventId) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
    const url = `${baseUrl}/events/${eventId}/registration-config`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }).then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          let errorData;
          try {
            errorData = JSON.parse(text);
          } catch (e) {
            errorData = { message: text };
          }

          const error: any = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
          error.response = {
            status: response.status,
            data: errorData,
            isDeactivated: response.status === 403
          };
          throw error;
        });
      }
      return response.json().then(result => ({ data: result }));
    });
  },

  // Logo management endpoints
  uploadLogo: (id, formData) => axios.post(`/events/${id}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: (id) => axios.delete(`/events/${id}/logo`),
  updateLogoConfig: (id, data) => axios.put(`/events/${id}/logo-config`, data),
  getLogoConfig: (id) => axios.get(`/events/${id}/logo-config`),
};

// Custom field endpoints
export const fieldAPI = {
  getAll: (eventId) => axios.get(`/events/${eventId}/custom-fields`),
  getById: (eventId, id) => axios.get(`/events/${eventId}/custom-fields/${id}`),
  create: (eventId, data) => axios.post(`/events/${eventId}/custom-fields`, data),
  update: (eventId, id, data) => axios.put(`/events/${eventId}/custom-fields/${id}`, data),
  updateVisibility: (eventId, id, data) => axios.put(`/events/${eventId}/custom-fields/${id}/visibility`, data),
  delete: (eventId, id) => axios.delete(`/events/${eventId}/custom-fields/${id}`),
  reorder: (eventId, fields) => axios.post(`/events/${eventId}/custom-fields/reorder`, { fields }),
};

// Attendee endpoints
export const attendeeAPI = {
  getAll: (eventId, params) => axios.get(`/events/${eventId}/attendees`, { params }),
  getById: (eventId, id) => axios.get(`/events/${eventId}/attendees/${id}`),
  create: (eventId, data) => axios.post(`/events/${eventId}/attendees`, data),
  update: (eventId, id, data) => axios.put(`/events/${eventId}/attendees/${id}`, data),
  delete: (eventId, id) => axios.delete(`/events/${eventId}/attendees/${id}`),
  checkIn: (eventId, id) => axios.post(`/events/${eventId}/attendees/${id}/check-in`),
  export: (eventId) => axios.get(`/events/${eventId}/attendees/export`, { responseType: 'blob' }),
  // Public self-registration (no auth required)
  selfRegister: (eventId, data, usherToken?: string) => {
    console.log('🚀 Self-registration data:', data);
    console.log('🚀 Event ID:', eventId);

    // Use fetch without authentication for public registration
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
    let url = `${baseUrl}/events/${eventId}/register`;
    if (usherToken) {
      url += `?usher_token=${encodeURIComponent(usherToken)}`;
    }

    console.log('🚀 Self-registration URL:', url);

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(data),
    }).then(response => {
      console.log('📡 Self-registration response status:', response.status);
      console.log('📡 Self-registration response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        return response.text().then(text => {
          console.error('❌ Self-registration error response:', text);

          // Try to parse as JSON, fallback to text
          let errorData;
          try {
            errorData = JSON.parse(text);
          } catch (e) {
            errorData = { message: text };
          }

          const error: any = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
          error.response = {
            status: response.status,
            data: errorData
          };
          throw error;
        });
      }

      return response.json().then(result => {
        console.log('✅ Self-registration success:', result);
        return { data: result }; // Wrap in data property to match axios format
      });
    });
  },
  bulkImport: (eventId, data) => axios.post(`/events/${eventId}/attendees/bulk-import`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  bulkCheckIn: (eventId, attendeeIds) => axios.post(`/events/${eventId}/attendees/bulk/check-in`, { attendee_ids: attendeeIds }),
  bulkExport: (eventId, data) => axios.post(`/events/${eventId}/attendees/bulk-export`, data, { responseType: 'blob' }),
  // QR Code verification (for check-in)
  verifyQR: (qrCode) => axios.get(`/attendees/verify/${qrCode}`),
  // Public attendee detail (for QR code scanning) - requires auth
  getPublicDetail: (attendeeId) => axios.get(`/attendees/${attendeeId}/public`),
  // Public attendee detail (for QR code scanning) - no auth required
  getPublicDetailNoAuth: (attendeeId, eventId = null) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
    let url = `${baseUrl}/attendees/${attendeeId}/public-no-auth`;
    if (eventId) {
      url += `?event=${eventId}`;
    }

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }).then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = JSON.parse(text);
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            // If response is not JSON, use the text as error message
            errorMessage = text || errorMessage;
          }
          throw new Error(errorMessage);
        });
      }
      return response.json();
    });
  },
};

// Badge endpoints
export const badgeAPI = {
  preview: (eventId, attendeeId) => axios.get(`/events/${eventId}/badges/preview?attendee_id=${attendeeId}`),
  generate: (eventId, data) => axios.post(`/events/${eventId}/badges/generate`, data, { responseType: 'blob' }),
  print: (eventId, data) => axios.post(`/events/${eventId}/badges/print`, data),
  printBatch: (eventId, data) => axios.post(`/events/${eventId}/badges/print`, data),
  getTemplates: (eventId) => axios.get(`/events/${eventId}/badges/templates`),
  saveTemplate: (eventId, data) => axios.post(`/events/${eventId}/badges/templates`, data),
  deleteTemplate: (eventId, templateId) => axios.delete(`/events/${eventId}/badges/templates/${templateId}`),
};

// Event assignment endpoints (multi-tenant)
export const assignmentAPI = {
  getEventAssignments: (eventId) => axios.get(`/events/${eventId}/assignments`),
  assignUser: (eventId, data) => axios.post(`/events/${eventId}/assignments`, data),
  updateAssignment: (eventId, assignmentId, data) => axios.put(`/events/${eventId}/assignments/${assignmentId}`, data),
  removeAssignment: (eventId, assignmentId) => axios.delete(`/events/${eventId}/assignments/${assignmentId}`),
  getUserEvents: () => axios.get('/user/events'),
};

// Analytics endpoints
export const analyticsAPI = {
  getDashboard: (eventId = null) => eventId ? axios.get(`/analytics/dashboard/${eventId}`) : axios.get('/analytics/dashboard'),
  getEventAnalytics: (eventId) => axios.get(`/analytics/events/${eventId}`),
  getDemographics: (eventId, params) => axios.get(`/analytics/attendees/${eventId}`, { params }),
  getAllEvents: () => axios.get('/analytics/all-events'),
  getAllUsers: () => axios.get('/analytics/all-users'),
  getEventAdmins: () => axios.get('/analytics/event-admins'),
  getAllAttendees: () => axios.get('/analytics/all-attendees'),
  // New: Get comprehensive field statistics for an event
  getFieldStatistics: (eventId) => axios.get(`/analytics/field-statistics/${eventId}`),
  getCrossTabulation: (eventId, field1, field2) => axios.get(`/analytics/cross-tabulation/${eventId}`, { params: { field1, field2 } }),
};

// Settings endpoints
export const settingsAPI = {
  getAll: () => axios.get('/settings'),
  update: (data) => axios.post('/settings', data),
  getSetting: (key) => axios.get(`/settings/${key}`),
  updateSetting: (key, data) => axios.put(`/settings/${key}`, data),
  getTheme: () => axios.get('/theme'),
  updateTheme: (data, scope = 'user') => axios.put('/settings/theme', { ...data, scope }),
  resetTheme: (scope = 'user') => axios.post('/settings/theme/reset', { scope }),
  getAllSettings: () => axios.get('/settings/all'),
  updateOrganization: (formData) => axios.post('/settings/organization', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
};


// Print settings endpoints
export const printSettingsAPI = {
  getSettings: (eventId) => axios.get(`/events/${eventId}/print-settings`),
  updateSettings: (eventId, settings) => axios.put(`/events/${eventId}/print-settings`, settings),
  resetSettings: (eventId) => axios.post(`/events/${eventId}/print-settings/reset`),
};

// Registration link endpoints
export const registrationLinkAPI = {
  getLink: (eventId) => axios.get(`/events/${eventId}/registration-link`),
  resetLink: (eventId) => axios.post(`/events/${eventId}/registration-link/reset`),
  getConfigByToken: (token) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
    const url = `${baseUrl}/registration/${token}`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }).then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          let errorData;
          try {
            errorData = JSON.parse(text);
          } catch (e) {
            errorData = { message: text || 'Unknown error occurred' };
          }
          const error: any = new Error(errorData.message || 'Registration configuration failed');
          error.status = response.status;
          error.response = { data: errorData, status: response.status };
          throw error;
        });
      }
      return response.json();
    });
  },
};

// Short URL endpoints
export const shortUrlAPI = {
  getAll: (eventId) => axios.get(`/events/${eventId}/short-urls`),
  create: (eventId, data) => axios.post(`/events/${eventId}/short-urls`, data),
  update: (eventId, shortUrlId, data) => axios.put(`/events/${eventId}/short-urls/${shortUrlId}`, data),
  delete: (eventId, shortUrlId) => axios.delete(`/events/${eventId}/short-urls/${shortUrlId}`),
  getAnalytics: (eventId, shortUrlId) => axios.get(`/events/${eventId}/short-urls/${shortUrlId}/analytics`),
};

// Event user assignment endpoints
export const eventUserAssignmentAPI = {
  getAll: (eventId) => axios.get(`/events/${eventId}/user-assignments`),
  create: (eventId, data) => axios.post(`/events/${eventId}/user-assignments`, data),
  update: (eventId, assignmentId, data) => axios.put(`/events/${eventId}/user-assignments/${assignmentId}`, data),
  delete: (eventId, assignmentId) => axios.delete(`/events/${eventId}/user-assignments/${assignmentId}`),
  searchUsers: (query) => axios.get(`/users/search`, { params: { q: query } }),
  getAvailableUsers: (eventId) => axios.get(`/events/${eventId}/available-users`),
};

// Health check endpoints
export const healthAPI = {
  check: () => axios.get('/health'),
  test: () => axios.get('/test'),
};

// Template download endpoints
export { templateAPI };

// User Guide endpoints
export const userGuideAPI = {
  getGuide: () => axios.get('/user-guide'),
  downloadGuidePdf: () => axios.get('/user-guide/pdf', { responseType: 'blob' }),
};

// Ticket endpoints
export const ticketAPI = {
  getAll: (eventId) => axios.get(`/events/${eventId}/ticket-types`),
  getPublicTickets: (eventId) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
    return fetch(`${baseUrl}/events/${eventId}/ticket-types/public`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    }).then(r => r.json());
  },
  create: (eventId, data) => axios.post(`/events/${eventId}/ticket-types`, data),
  update: (eventId, id, data) => axios.put(`/events/${eventId}/ticket-types/${id}`, data),
  delete: (eventId, id) => axios.delete(`/events/${eventId}/ticket-types/${id}`),
  scanTicket: (eventId, token) => axios.post(`/events/${eventId}/scan-ticket`, { token }),
  initiatePayment: (eventId, data) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
    return fetch(`${baseUrl}/events/${eventId}/ticket-payments/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => r.json());
  },
  verifyPayment: (eventId, reference) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
    return fetch(`${baseUrl}/events/${eventId}/ticket-payments/verify/${reference}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    }).then(r => r.json());
  },
};
